// A private thread with each of your two seating neighbours, open from night 2 onward. A
// Storyteller gets the combined log of everything sent across the game instead of threads of
// their own — they have no seat, so they have no neighbours.
//
// Messages arrive with a short delay proportional to their length, which the server applies; a
// player's own messages show immediately, and the other end's appear when they land.

import { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import type { GameView } from "@cc/shared";
import { useGame } from "../../state/game.js";
import { cn } from "../../lib/ui.js";
import { Button, Input } from "../ui/primitives.js";
import { Panel } from "./Panel.js";

function timeOf(ms: number): string {
  return new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function NeighborChat({
  view,
  open,
  onOpenChange,
}: {
  view: GameView;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const send = useGame((s) => s.send);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  // Toast, unread count and a pulsing edge when a neighbour's message lands while the panel is
  // collapsed. The first render only records what is already there, so a refresh doesn't replay the
  // backlog.
  const seen = useRef<Set<string> | null>(null);
  const [unread, setUnread] = useState(0);
  const incoming = view.neighborThreads.flatMap((t) =>
    t.messages.filter((m) => m.fromSeatId !== view.you.seatId).map((m) => ({ ...m, from: t.otherSeatName })),
  );
  const incomingKey = incoming.map((m) => m.id).join(",");
  useEffect(() => {
    if (seen.current === null) {
      seen.current = new Set(incoming.map((m) => m.id));
      return;
    }
    const fresh = incoming.filter((m) => !seen.current!.has(m.id));
    if (fresh.length === 0) return;
    for (const m of fresh) seen.current.add(m.id);
    if (!open) setUnread((n) => n + fresh.length);
    const last = fresh[fresh.length - 1]!;
    const notice = { kind: "info" as const, text: `${last.from}: ${last.text}`, id: Date.now() };
    useGame.setState((s) => ({ notices: [...s.notices, notice].slice(-4) }));
    setTimeout(() => useGame.getState().dismissNotice(notice.id), 6000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingKey]);
  useEffect(() => {
    if (open) setUnread(0);
  }, [open]);

  const closedTonight = view.phase.kind === "night" && view.phase.number <= 1;
  const threads = view.neighborThreads;
  const active = threads.find((t) => t.chatId === activeChatId) ?? threads[0] ?? null;

  if (view.you.isStoryteller) {
    const log = view.storyteller?.neighborLog ?? [];
    return (
      <Panel
        title="Neighbour Log"
        badge={<span className="text-sm text-parchment-dim">{log.length}</span>}
        open={open}
        onOpenChange={onOpenChange}
      >
        {log.length === 0 ? (
          <p className="py-2 text-sm text-parchment-dim">Nothing sent yet.</p>
        ) : (
          <ul className="space-y-1.5">
            {log.map((message) => (
              <li key={message.id} className="text-xs">
                <span className="text-parchment-dim">{timeOf(message.sentAt)} </span>
                <span className="text-brass">{message.fromSeatName}</span>
                <span className="text-parchment-dim"> → {message.toSeatName}: </span>
                <span className="text-parchment">{message.text}</span>
              </li>
            ))}
          </ul>
        )}
      </Panel>
    );
  }

  async function submit(): Promise<void> {
    if (!active || !draft.trim()) return;
    const text = draft.trim();
    setDraft("");
    await send("player:neighborMessage", { chatId: active.chatId, text });
  }

  return (
    <Panel
      title="Neighbours"
      badge={
        unread > 0 && (
          <span className="rounded-full bg-brass px-1.5 text-xs font-bold text-ink">{unread}</span>
        )
      }
      open={open}
      onOpenChange={onOpenChange}
      className={unread > 0 ? "cc-alert border-brass" : undefined}
    >
      {threads.length === 0 ? (
        <p className="py-2 text-sm text-parchment-dim">You have no neighbours yet.</p>
      ) : (
        <div className="space-y-2">
          <div className="flex gap-1">
            {threads.map((thread) => (
              <Button
                key={thread.chatId}
                size="sm"
                variant={active?.chatId === thread.chatId ? "primary" : "secondary"}
                onClick={() => setActiveChatId(thread.chatId)}
              >
                {thread.otherSeatName}
              </Button>
            ))}
          </div>

          <ul className="max-h-56 space-y-1 overflow-y-auto cc-scroll">
            {(active?.messages ?? []).map((message) => {
              const mine = message.fromSeatId === view.you.seatId;
              return (
                <li
                  key={message.id}
                  className={cn(
                    "rounded px-2 py-1 text-xs",
                    mine ? "bg-brass/15 text-parchment" : "bg-ink text-parchment",
                  )}
                >
                  <span className="text-parchment-dim">{timeOf(message.sentAt)} </span>
                  {message.text}
                </li>
              );
            })}
            {(active?.messages.length ?? 0) === 0 && (
              <li className="text-xs text-parchment-dim">Nothing here yet.</li>
            )}
          </ul>

          {closedTonight ? (
            <p className="text-xs text-parchment-dim">Neighbour chat opens from night 2 onward.</p>
          ) : (
            <form
              className="flex gap-1"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <Input
                value={draft}
                maxLength={500}
                placeholder={`Message ${active?.otherSeatName ?? ""}`}
                onChange={(e) => setDraft(e.target.value)}
              />
              <Button type="submit" size="sm" variant="primary" disabled={!draft.trim()}>
                <Send className="size-3.5" />
              </Button>
            </form>
          )}
        </div>
      )}
    </Panel>
  );
}
